export const SETUP_STEP_KEYS = {
    ADD_SERVICE: 'addService',
    GET_NUMBERS: 'getNumbers',
    MANAGE_NUMBERS: 'manageNumbers',
    ENABLE_USERS: 'enableUsers',
    MAKE_CALLS: 'makeCalls',
};

export const SETUP_STEPS = [
    {
        key: SETUP_STEP_KEYS.ADD_SERVICE,
        label: 'Add a service',
        description:
            'Add at least one UCaaS service to your company so TCAP can automate set up and management.',
    },
    {
        key: SETUP_STEP_KEYS.GET_NUMBERS,
        label: 'Get numbers',
        description: 'Order, port or allocate phone numbers to your company.',
    },
    {
        key: SETUP_STEP_KEYS.MANAGE_NUMBERS,
        label: 'Manage numbers',
        description:
            'Assign your numbers to a trunk or service so they can route calls.',
    },
    {
        key: SETUP_STEP_KEYS.ENABLE_USERS,
        label: 'Enable users',
        description:
            'Assign numbers to users on your platforms so they can make and receive calls.',
    },
    {
        key: SETUP_STEP_KEYS.MAKE_CALLS,
        label: 'Make calls',
        description:
            'Completes automatically once call records appear for your company.',
    },
];

export const REQUEST_NUMBERS_NOTE =
    'Contact your service provider to request numbers.';

/**
 * (3154) Where a step sends the user, or null when there is nothing to act on
 * from the dashboard. The label is the "Go to …" text: for now the tooltip's
 * Learn how button shows and runs it, and once Learn how opens the step's
 * help & training it becomes that modal's button instead.
 *
 * - Get numbers only links out when the wholesaler has public pool numbers
 *   the company can add; otherwise the step shows REQUEST_NUMBERS_NOTE
 * - Enable users needs a service first
 * - Make calls completes on its own and never offers one
 *
 * @returns {{ label: string } | null}
 */
export const getStepAction = (
    stepKey,
    { hasServices, hasPublicPoolNumbers },
) => {
    switch (stepKey) {
        case SETUP_STEP_KEYS.ADD_SERVICE:
            return { label: 'Add New Service' };
        case SETUP_STEP_KEYS.GET_NUMBERS:
            return hasPublicPoolNumbers ? { label: 'Go to Numbers' } : null;
        case SETUP_STEP_KEYS.MANAGE_NUMBERS:
            return { label: 'Go to Numbers' };
        case SETUP_STEP_KEYS.ENABLE_USERS:
            return hasServices ? { label: 'Go to Users' } : null;
        default:
            return null;
    }
};

/**
 * (3130) A number is managed once it is healthy (status 0) and sits on a
 * trunk. The numbers summary reports an unassigned number as trunkName
 * 'disabled' or null, the same rule the Numbers page status column applies.
 */
export const isManagedNumber = (number) => {
    const trunkName = number?.trunkName;
    return (
        number?.status === 0 &&
        Boolean(trunkName) &&
        trunkName.toLowerCase() !== 'disabled'
    );
};

/**
 * (3131) Whether at least one user has a number assigned (a user profile).
 *
 * @param {object} usersPayload - the /TCAPUsers/{companyID} response
 */
export const hasUserWithNumber = (usersPayload) =>
    (usersPayload?.assignedPlatformUsers ?? 0) > 0 ||
    (usersPayload?.tcapUsers ?? []).some((user) =>
        Boolean(user?.telephoneNumber),
    );

/**
 * (3145) Works out which onboarding milestones are done and which one the
 * company should tackle next.
 *
 * The last step ("Make calls") completes on its own once call records exist,
 * so it is never offered as the current step.
 *
 * @param {object} status
 * @param {boolean} status.hasServices - the company has at least one service
 * @param {object[]} status.numbers - the company's numbers (numbers summary)
 * @param {object} status.users - the /TCAPUsers/{companyID} response
 * @param {boolean} status.hasCallRecords - calls were made in the last 30 days
 * @param {boolean} status.hasPublicPoolNumbers - public pool numbers can be added
 * @returns {{ steps: object[], currentStepIndex: number, isComplete: boolean }}
 */
export const resolveSetupProgress = ({
    hasServices,
    numbers,
    users,
    hasCallRecords,
    hasPublicPoolNumbers,
}) => {
    const numberList = Array.isArray(numbers) ? numbers : [];
    const doneByKey = {
        [SETUP_STEP_KEYS.ADD_SERVICE]: Boolean(hasServices),
        [SETUP_STEP_KEYS.GET_NUMBERS]: numberList.length > 0,
        [SETUP_STEP_KEYS.MANAGE_NUMBERS]: numberList.some(isManagedNumber),
        [SETUP_STEP_KEYS.ENABLE_USERS]: hasUserWithNumber(users),
        [SETUP_STEP_KEYS.MAKE_CALLS]: Boolean(hasCallRecords),
    };

    const steps = SETUP_STEPS.map((step) => {
        const action = getStepAction(step.key, {
            hasServices: Boolean(hasServices),
            hasPublicPoolNumbers: Boolean(hasPublicPoolNumbers),
        });
        return {
            ...step,
            done: doneByKey[step.key],
            action,
            note:
                step.key === SETUP_STEP_KEYS.GET_NUMBERS && !action
                    ? REQUEST_NUMBERS_NOTE
                    : null,
        };
    });
    const firstPending = steps.findIndex((step) => !step.done);
    const currentStepIndex =
        firstPending === steps.length - 1 ? -1 : firstPending;

    return {
        steps,
        currentStepIndex,
        isComplete: firstPending === -1,
    };
};
